/**
 * MCP Tools - Tool implementations for the Moviefone chat assistant.
 *
 * Each tool is a pure TypeScript function that extracts data from the existing
 * WASM MoviefoneAnalysis object. The only exception is getVixContext, which
 * fetches VIX data from the stream API.
 */

import { DefaultService as StreamAPI } from 'gen/stream-api/requests/services.gen';
import { MoviefoneAnalysis } from 'gen/stream-api/requests/types.gen';
import {
  resolveStyleForExpiryDate,
  type ExerciseStylesRecord,
} from './moviefoneExerciseStyles';
import { black76 } from 'sg-risk-engine';

// ---------------------------------------------------------------------------
// Tool 1: get_vol_forecast
// ---------------------------------------------------------------------------

export function getVolForecast(data: MoviefoneAnalysis): object {
  return data.forecast ?? { error: 'forecast data unavailable' };
}

// ---------------------------------------------------------------------------
// Tool 2: get_vol_forecast_full (full analysis object)
// ---------------------------------------------------------------------------

export function getVolForecastFull(data: MoviefoneAnalysis): object {
  return data;
}

// ---------------------------------------------------------------------------
// Tool 3: get_vrp_term_structure
// ---------------------------------------------------------------------------

export function getVrpTermStructure(data: MoviefoneAnalysis): object {
  return data.vrp_term_structure ?? { error: 'VRP term structure unavailable' };
}

// ---------------------------------------------------------------------------
// Tool 4: get_vix_context (async, fetches from stream API)
// ---------------------------------------------------------------------------

function classifyVixRegime(vix: number): {
  label: string;
  description: string;
} {
  if (vix < 15) {
    return { label: 'LOW_VOL', description: 'VIX below 15 - complacency zone' };
  }
  if (vix < 20) {
    return { label: 'NORMAL_VOL', description: 'VIX 15-20 - normal market' };
  }
  if (vix < 25) {
    return {
      label: 'ELEVATED_VOL',
      description: 'VIX 20-25 - elevated concern',
    };
  }
  if (vix < 30) {
    return { label: 'HIGH_VOL', description: 'VIX 25-30 - high fear' };
  }
  return { label: 'EXTREME_VOL', description: 'VIX 30+ - extreme fear' };
}

export async function getVixContext(): Promise<object> {
  try {
    const resp = await StreamAPI.getVixQuote();
    const vixLevel = resp.items?.[0]?.close ?? 0;
    const regime = classifyVixRegime(vixLevel);
    return {
      text: `${regime.label}: VIX at ${vixLevel.toFixed(1)}`,
      vix: vixLevel,
      vix3m: null,
      vvix: null,
      regime: regime.label,
      regime_desc: regime.description,
      term_structure: 'flat',
      ts_slope: null,
      etp_roll_note: null,
      vvix_note: null,
      symbol_note: null,
    };
  } catch (err) {
    return {
      error: `Failed to fetch VIX data: ${err instanceof Error ? err.message : 'unknown error'}`,
    };
  }
}

// ---------------------------------------------------------------------------
// Tool 5: get_regime_status
// ---------------------------------------------------------------------------

export function getRegimeStatus(data: MoviefoneAnalysis): object {
  const vol = data.volatility;
  return {
    regime: data.regime,
    tvtp_regime: data.tvtp_regime,
    volatility_snapshot: {
      atm_iv_30d: vol.atm_iv_30d,
      realized_vol: vol.realized_vol.rv_20d,
      vrp: vol.realized_vol.vrp,
      vrp_regime: vol.realized_vol.vrp_regime,
    },
  };
}

// ---------------------------------------------------------------------------
// Tool 6: get_implied_earnings_move
// ---------------------------------------------------------------------------

export function getImpliedEarningsMove(data: MoviefoneAnalysis): object {
  const eventSignal = data.volatility.event_signal;
  if (eventSignal == null || !eventSignal.detected) {
    return { ticker: data.symbol, detected: false };
  }
  return {
    ticker: data.symbol,
    detected: true,
    days_to_event: eventSignal.days_to_event,
    confidence: eventSignal.confidence,
    forward_vol_ratio: eventSignal.forward_vol_ratio,
    event_source: eventSignal.event_source,
    is_post_event: eventSignal.is_post_event,
    current_atm_iv_30d: data.volatility.atm_iv_30d,
  };
}

// ---------------------------------------------------------------------------
// Tool 7: get_sgoi_positioning
// ---------------------------------------------------------------------------

export function getSgoiPositioning(data: MoviefoneAnalysis): object {
  if (data.synth_oi == null) {
    return { ticker: data.symbol, error: 'no synth_oi data' };
  }
  return {
    ticker: data.symbol,
    quote_date: data.synth_oi.quote_date,
    spot: data.synth_oi.spot,
    customer: data.synth_oi.customer,
    firm: data.synth_oi.firm,
    market_maker: data.synth_oi.market_maker,
    iv_rank: data.synth_oi.iv_rank,
    skew_rank: data.synth_oi.skew_rank,
    entity_direction: data.synth_oi.entity_direction,
    key_levels: data.synth_oi.key_levels,
  };
}

// ---------------------------------------------------------------------------
// Tool 8: get_scenario_pnl (needs WASM black76)
// ---------------------------------------------------------------------------

export interface ScenarioLeg {
  call_or_put: string;
  strike: number;
  expiration: string;
  quantity: number;
  entry_price: number;
}

export function getScenarioPnl(
  data: MoviefoneAnalysis,
  params: {
    legs: ScenarioLeg[];
    spot_shocks_pct?: number[];
    vol_shocks_vpts?: number[];
    exercise_styles?: ExerciseStylesRecord;
  },
): object {
  const spotShocks = params.spot_shocks_pct ?? [
    -0.1, -0.05, -0.02, 0, 0.02, 0.05, 0.1,
  ];
  const volShocks = params.vol_shocks_vpts ?? [-0.05, -0.02, 0, 0.02, 0.05];

  const spot = data.spot;
  const grid: number[][] = [];

  for (const volShock of volShocks) {
    const row: number[] = [];
    for (const spotShock of spotShocks) {
      let totalPnl = 0;
      for (const leg of params.legs) {
        const shockedSpot = spot * (1 + spotShock);
        const baseIv = data.volatility.atm_iv_30d;
        const shockedIv = baseIv + volShock;
        const isCall = leg.call_or_put.toLowerCase().startsWith('c');
        const exerciseStyle = resolveStyleForExpiryDate(
          params.exercise_styles,
          leg.expiration,
        );
        try {
          const result = black76(
            shockedSpot,
            leg.strike,
            0.5,
            0.043_3,
            shockedIv,
            isCall,
            exerciseStyle,
          );
          totalPnl += (result.price - leg.entry_price) * leg.quantity;
        } catch {
          // Skip legs that fail pricing
        }
      }
      row.push(totalPnl);
    }
    grid.push(row);
  }

  const flat = grid.flat();
  return {
    ticker: data.symbol,
    spot_shocks_pct: spotShocks,
    vol_shocks_vpts: volShocks,
    grid,
    max_pnl: flat.length > 0 ? Math.max(...flat) : 0,
    min_pnl: flat.length > 0 ? Math.min(...flat) : 0,
  };
}

// ---------------------------------------------------------------------------
// Tool 9: get_surface_edge_recommendation
// ---------------------------------------------------------------------------

// Local interface for surface edge data (not defined in types/moviefone.ts because it's untyped in WASM)
interface SurfaceEdge {
  top_spreads?: Array<{ spread_type: string }>;
  top_legs?: object[];
  recommended?: object | null;
  stale_warning?: string | null;
}

export function getSurfaceEdgeRecommendation(
  data: MoviefoneAnalysis,
  params: { spot_change_pct: number; hold_days?: number; vol_signal?: string },
): object {
  if (data.surface_edge == null) {
    return { ticker: data.symbol, error: 'surface edge data unavailable' };
  }

  const surfaceEdge = data.surface_edge as SurfaceEdge;
  let spreads = (surfaceEdge.top_spreads ?? []) as Array<{
    spread_type: string;
  }>;

  if (params.vol_signal === 'short_vol') {
    spreads = spreads.filter((s) =>
      ['BearCall', 'BullPut'].includes(s.spread_type),
    );
  } else if (params.vol_signal === 'long_vol') {
    spreads = spreads.filter((s) =>
      ['BullCall', 'BearPut'].includes(s.spread_type),
    );
  }

  return {
    ticker: data.symbol,
    spot_change_pct: params.spot_change_pct,
    hold_days: params.hold_days ?? 5,
    vol_signal: params.vol_signal,
    top_legs: surfaceEdge.top_legs ?? [],
    top_spreads: spreads,
    recommended: surfaceEdge.recommended ?? null,
    stale_warning: surfaceEdge.stale_warning ?? null,
  };
}

// ---------------------------------------------------------------------------
// Tool 10: get_strategy_recommendation
// ---------------------------------------------------------------------------

export function getStrategyRecommendation(data: MoviefoneAnalysis): object {
  const top3 = data.strategies.slice(0, 3);
  return {
    ticker: data.symbol,
    strategies: top3,
    regime: data.regime.regime,
    vol_bias: data.strategy_rules?.vol_bias ?? null,
    strategy_catalog: data.strategy_catalog,
  };
}

// ---------------------------------------------------------------------------
// Tool 11: get_available_strategies
// ---------------------------------------------------------------------------

export function getAvailableStrategies(data: MoviefoneAnalysis): object {
  return {
    strategies: data.strategy_catalog,
  };
}

// ---------------------------------------------------------------------------
// Tool 12: build_spotgamma_link (pure JS URL builder)
// ---------------------------------------------------------------------------

export function buildSpotgammaLink(params: {
  symbol: string;
  legs: Array<{
    expiration: string;
    call_or_put: string;
    strike: number;
    quantity: number;
    premium?: number;
  }>;
}): string {
  const symbol = params.symbol.toUpperCase();
  const base = 'https://spotgamma.com/options-calculator/';

  function snapToWeekday(dateStr: string): string {
    // Snap expiration to the preceding Friday (standard options expiry)
    const date = new Date(dateStr + 'T16:00:00-05:00');
    const day = date.getDay();
    if (day === 0) {
      date.setDate(date.getDate() - 2);
    } else if (day === 6) {
      date.setDate(date.getDate() - 1);
    }
    return date.toISOString().split('T')[0];
  }

  const legStrings = params.legs.map((leg) => {
    const snapped = snapToWeekday(leg.expiration);
    const epochMs = new Date(snapped + 'T16:00:00-05:00').getTime();
    const cp = leg.call_or_put.toLowerCase().startsWith('c') ? 'C' : 'P';
    return `${symbol}_${epochMs}_${cp}_${leg.strike}_${leg.quantity}${leg.premium != null ? `_${leg.premium}` : ''}`;
  });

  return `${base}?sym=${symbol}&legs=${legStrings.join(',')}`;
}
